import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";

// Safe sessionStorage wrapper to prevent crashes in private browsing
const safeSessionGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Silently fail if storage is unavailable
  }
};

// In development, ensure any previously-registered service worker is removed.
// Stale SW caches can break Vite's dynamic imports ("Failed to fetch dynamically imported module").
// NOTE: unregistering does NOT immediately stop a SW from controlling the current page;
// a one-time reload after cleanup is the most reliable fix.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  const didReloadKey = "__dev_sw_cleanup_reloaded_v2__";

  navigator.serviceWorker.getRegistrations().then(async (regs) => {
    const hadRegs = regs.length > 0;

    await Promise.all(regs.map((reg) => reg.unregister()));

    let hadCaches = false;
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        hadCaches = keys.length > 0;
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // caches API may be restricted in some environments
    }

    // Reload once if we actually cleaned anything.
    if ((hadRegs || hadCaches) && !safeSessionGet(didReloadKey)) {
      safeSessionSet(didReloadKey, "1");
      window.location.reload();
    }
  });
}

// Handle dynamic import failures by triggering a reload
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('Failed to fetch dynamically imported module')) {
    const reloadKey = '__dynamic_import_reload__';
    if (!safeSessionGet(reloadKey)) {
      safeSessionSet(reloadKey, '1');
      window.location.reload();
    }
  }
});

createRoot(document.getElementById("root")!).render(<App />);
